import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  appointmentsApi,
  type AppointmentRescheduleRequestDto,
  type CancelAppointmentBody,
  type PortalAppointmentDto,
  type RequestAppointmentBody,
  type RequestClinicAppointmentBody,
  type RequestRescheduleBody,
} from '../infrastructure/appointments.api';
import { appointmentKeys } from './appointments.query';

function useAppointmentsInvalidation() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
}

/** Request a new appointment. */
export function useRequestAppointmentMutation() {
  const invalidate = useAppointmentsInvalidation();
  return useMutation<PortalAppointmentDto, unknown, RequestAppointmentBody>({
    mutationFn: (body) => appointmentsApi.request(body),
    onSuccess: invalidate,
  });
}

/** Cancel an existing appointment. */
export function useCancelAppointmentMutation() {
  const invalidate = useAppointmentsInvalidation();
  return useMutation<PortalAppointmentDto, unknown, { appointmentId: string; body: CancelAppointmentBody }>({
    mutationFn: ({ appointmentId, body }) => appointmentsApi.cancel(appointmentId, body),
    onSuccess: invalidate,
  });
}

/** Request a reschedule for an existing appointment (clinic reviews and approves/rejects). */
export function useRequestRescheduleMutation() {
  const invalidate = useAppointmentsInvalidation();
  return useMutation<
    AppointmentRescheduleRequestDto | PortalAppointmentDto,
    unknown,
    { appointmentId: string; status: PortalAppointmentDto['status']; body: RequestRescheduleBody }
  >({
    mutationFn: ({ appointmentId, status, body }) =>
      status === 'Requested'
        ? appointmentsApi.rescheduleRequested(appointmentId, body)
        : appointmentsApi.requestReschedule(appointmentId, body),
    onSuccess: invalidate,
  });
}

export function useRequestClinicAppointmentMutation() {
  const invalidate = useAppointmentsInvalidation();
  return useMutation<
    PortalAppointmentDto,
    unknown,
    { clinicId: string; body: RequestClinicAppointmentBody }
  >({
    mutationFn: ({ clinicId, body }) => appointmentsApi.requestClinicAppointment(clinicId, body),
    onSuccess: invalidate,
  });
}
